/**
 * t373u: HR records stage 2 - documents (ID, right-to-work evidence, contracts, certificates, ...) and UK
 * right-to-work checks. Shared by the API and the three apps so the labels, rules and wording match everywhere.
 *
 * Files live in private storage (collab.attachments, purpose 'hr_document') and are only ever read through the HR
 * API, which checks access, hands out a 60-second signed URL and writes the audit log. Deleting is soft (kept with a
 * retention note); the purge comes with stage 4.
 */
import { DateTime } from 'luxon';
import { formatGB } from './gbDate';
import { MAX_UPLOAD_BYTES, UPLOAD_TOO_BIG } from './uploadLimits';
import { uploadTypeOf } from './uploadTypes';

// ------------------------------------------------------------------------------------------------ documents
export const HR_DOC_TYPES = ['passport', 'driving_licence', 'rtw_evidence', 'proof_of_address', 'contract', 'certificate', 'other'] as const;
export type HrDocType = (typeof HR_DOC_TYPES)[number];
export const HR_DOC_TYPE_LABEL: Record<HrDocType, string> = {
  passport: 'Passport', driving_licence: 'Driving licence', rtw_evidence: 'Right-to-work evidence (BRP, eVisa or share-code result)',
  proof_of_address: 'Proof of address', contract: 'Contract', certificate: 'Certificate', other: 'Other document',
};
/** Short labels for lists and pickers. */
export const HR_DOC_TYPE_SHORT: Record<HrDocType, string> = {
  passport: 'Passport', driving_licence: 'Driving licence', rtw_evidence: 'Right-to-work evidence', proof_of_address: 'Proof of address',
  contract: 'Contract', certificate: 'Certificate', other: 'Other',
};
/** Identity and right-to-work documents (what the onboarding step can ask for first). */
export const HR_ID_DOC_TYPES: readonly HrDocType[] = ['passport', 'driving_licence', 'rtw_evidence'];
export const isHrDocType = (v: unknown): v is HrDocType => typeof v === 'string' && (HR_DOC_TYPES as readonly string[]).includes(v);

export const HR_DOC_VISIBILITIES = ['employee', 'confidential'] as const;
export type HrDocVisibility = (typeof HR_DOC_VISIBILITIES)[number];
export const HR_DOC_VISIBILITY_LABEL: Record<HrDocVisibility, string> = { employee: 'They can see it', confidential: 'HR only' };

/** PDF and photos only (the 50 MB limit is the same as every other upload). */
export const HR_DOC_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'] as const;
export const HR_DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic';
export const HR_DOC_REFUSED = 'HR documents can be a PDF or a photo (JPEG, PNG or HEIC).';
/** The stored type of an allowed HR document, or null. */
export function hrDocMime(fileName: string, contentType: string | null | undefined): string | null {
  const t = uploadTypeOf(fileName, contentType);
  return t && (HR_DOC_MIME_TYPES as readonly string[]).includes(t.mime) ? t.mime : null;
}
/** What's wrong with a file before any bytes are sent (type, then size), or null. */
export function hrDocProblem(f: { name: string; type?: string | null; size: number }): string | null {
  if (!hrDocMime(f.name, f.type)) return HR_DOC_REFUSED;
  if (f.size > MAX_UPLOAD_BYTES) return UPLOAD_TOO_BIG;
  return null;
}

export interface HrDocument {
  id: string; user_id: string; type: HrDocType; file_name: string; content_type: string; size_bytes: number;
  expires_on: string | null; notes: string | null; visibility: HrDocVisibility;
  uploaded_at: string; uploaded_by_name: string | null; self_uploaded: boolean;
  checked_at: string | null; checked_by_name: string | null;
  /** Evidence for a right-to-work check (it can't be deleted by the person then). */
  rtw_evidence: boolean;
  can_edit: boolean; can_delete: boolean;
}
export interface HrDocumentInput { attachment_id?: string; type?: HrDocType; expires_on?: string | null; notes?: string | null; visibility?: HrDocVisibility; checked?: boolean }

// ------------------------------------------------------------------------------------------------ right to work
export const RTW_CHECK_TYPES = ['manual', 'online', 'idsp'] as const;
export type RtwCheckType = (typeof RTW_CHECK_TYPES)[number];
export const RTW_CHECK_TYPE_LABEL: Record<RtwCheckType, string> = {
  manual: 'Manual document check', online: 'Online Home Office check (share code)', idsp: 'Identity service provider (IDSP)',
};
export const RTW_RESULTS = ['unlimited', 'time_limited', 'not_eligible'] as const;
export type RtwResult = (typeof RTW_RESULTS)[number];
export const RTW_RESULT_LABEL: Record<RtwResult, string> = {
  unlimited: 'Can work, no time limit', time_limited: 'Can work, time-limited', not_eligible: 'Not allowed to work',
};

export interface HrRtwCheck {
  id: string; user_id: string; check_type: RtwCheckType; checked_on: string; checked_by: string | null; checked_by_name: string | null;
  share_code: string | null; share_code_on_file: boolean; result: RtwResult; follow_up_on: string | null;
  evidence_document_id: string | null; evidence_label: string | null; notes: string | null;
  recorded_at: string; recorded_by_name: string | null;
}
export interface HrRtwInput {
  check_type: RtwCheckType; checked_on: string; checked_by?: string | null; share_code?: string | null; result: RtwResult;
  follow_up_on?: string | null; evidence_document_id?: string | null; notes?: string | null;
}

/** A Home Office share code: 9 letters and numbers (shown in threes). Spaces and dashes are ignored. */
export function normaliseShareCode(raw: string | null | undefined): string | null {
  const s = (raw ?? '').toUpperCase().replace(/[\s-]+/g, '');
  return /^[A-Z0-9]{9}$/.test(s) ? s : null;
}
export const formatShareCode = (s: string) => `${s.slice(0, 3)} ${s.slice(3, 6)} ${s.slice(6, 9)}`;
export const maskShareCode = (last3: string) => `••• ••• ${last3}`;

export function rtwDraftErrors(d: Partial<HrRtwInput>, today = hrToday()): string[] {
  const out: string[] = [];
  if (!d.check_type || !(RTW_CHECK_TYPES as readonly string[]).includes(d.check_type)) out.push('Choose how it was checked');
  if (!d.checked_on || !isIsoDate(d.checked_on)) out.push('Enter the date it was checked');
  else if (d.checked_on > today) out.push("The check date can't be in the future");
  if (!d.result || !(RTW_RESULTS as readonly string[]).includes(d.result)) out.push('Choose the result');
  if (d.check_type === 'online' && !d.share_code) out.push('Enter the share code for an online check');
  if (d.share_code && !normaliseShareCode(d.share_code)) out.push('A share code is 9 letters and numbers');
  if (d.result === 'time_limited' && !d.follow_up_on) out.push('Enter when their permission ends (the follow-up date)');
  if (d.follow_up_on && !isIsoDate(d.follow_up_on)) out.push('Enter a valid follow-up date');
  return out;
}
export function hrDocDraftErrors(d: { type?: string | null; expires_on?: string | null; notes?: string | null }): string[] {
  const out: string[] = [];
  if (!isHrDocType(d.type)) out.push('Choose what kind of document it is');
  if (d.expires_on && !isIsoDate(d.expires_on)) out.push('Enter a valid expiry date');
  if ((d.notes ?? '').length > HR_DOC_NOTES_MAX) out.push(`Notes can be ${HR_DOC_NOTES_MAX} characters at most`);
  return out;
}
export const HR_DOC_NOTES_MAX = 500;

// ------------------------------------------------------------------------------------------------ expiry
/** Reminders go this many days ahead (the smallest one reached), then once more when it has expired. */
export const HR_EXPIRY_LEADS = [60, 30, 7] as const;
/** The Expiring soon list looks this far ahead. */
export const HR_EXPIRING_DAYS = 90;
/** Expired items stay on the Expiring soon list for this long (the one "expired" reminder only goes in the first 14 days). */
export const HR_EXPIRED_GRACE_DAYS = 30;

const isIsoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && DateTime.fromISO(s).isValid;
/** Today in Europe/London as YYYY-MM-DD. */
export const hrToday = (now: Date = new Date()) => DateTime.fromJSDate(now).setZone('Europe/London').toISODate()!;
export const hrDaysUntil = (date: string, today = hrToday()) => Math.round(DateTime.fromISO(date).diff(DateTime.fromISO(today), 'days').days);
/** The reminder stage for a date: the smallest lead time reached (60, 30, 7), 0 once it has passed, or null if not due yet. */
export function hrExpiryStage(daysLeft: number): number | null {
  if (daysLeft < 0) return 0;
  let stage: number | null = null;
  for (const l of HR_EXPIRY_LEADS) if (daysLeft <= l) stage = l;
  return stage;
}
/** "3 Dec 2026" (en-GB). */
export const hrDate = (date: string) => formatGB(DateTime.fromISO(date, { zone: 'Europe/London' }), 'd MMM yyyy');
/** "Expires 3 Dec 2026", "Expires in 5 days", "Expires today", "Expired 2 days ago". */
export function hrExpiryText(date: string, today = hrToday(), verb: 'Expires' | 'Follow up' = 'Expires'): string {
  const n = hrDaysUntil(date, today);
  const past = verb === 'Expires' ? 'Expired' : 'Follow-up was due';
  if (n < 0) return `${past} ${-n === 1 ? 'yesterday' : `${-n} days ago`}`;
  if (n === 0) return `${verb} today`;
  if (n <= 31) return `${verb} in ${n} day${n === 1 ? '' : 's'}`;
  return `${verb} ${hrDate(date)}`;
}

export interface HrExpiringRow {
  user_id: string; display_name: string; kind: 'document' | 'rtw'; id: string;
  /** "Passport" / "Right to work". */
  label: string; date: string; days_left: number;
}
/** Muted second line for an expiring item. */
export const hrExpiringLine = (r: Pick<HrExpiringRow, 'kind' | 'date'>, today = hrToday()) =>
  hrExpiryText(r.date, today, r.kind === 'rtw' ? 'Follow up' : 'Expires');

/** One document's muted line: "passport.pdf · Expires 3 Dec 2026 · HR only". */
export function hrDocLine(d: Pick<HrDocument, 'file_name' | 'expires_on' | 'visibility'>, today = hrToday(), showVisibility = true): string {
  return [d.file_name, d.expires_on ? hrExpiryText(d.expires_on, today) : null, showVisibility && d.visibility === 'confidential' ? 'HR only' : null]
    .filter(Boolean).join(' · ');
}
/** The right-to-work summary rows the three apps show for the latest check. */
export function rtwRows(c: HrRtwCheck, today = hrToday()): { key: string; label: string; value: string }[] {
  const rows = [
    { key: 'result', label: 'Result', value: RTW_RESULT_LABEL[c.result] },
    { key: 'check_type', label: 'How', value: RTW_CHECK_TYPE_LABEL[c.check_type] },
    { key: 'checked_on', label: 'Checked', value: `${hrDate(c.checked_on)}${c.checked_by_name ? ` by ${c.checked_by_name}` : ''}` },
  ];
  if (c.follow_up_on) {
    // Only add how long is left when it's close or past ("15 Jan 2027", "15 Nov 2026 · in 21 days").
    const n = hrDaysUntil(c.follow_up_on, today), soon = n <= 31;
    const left = n < 0 ? `${-n === 1 ? 'yesterday' : `${-n} days ago`}` : n === 0 ? 'today' : `in ${n} day${n === 1 ? '' : 's'}`;
    rows.push({ key: 'follow_up_on', label: 'Follow up', value: soon ? `${hrDate(c.follow_up_on)} · ${left}` : hrDate(c.follow_up_on) });
  }
  if (c.share_code_on_file) rows.push({ key: 'share_code', label: 'Share code', value: c.share_code ?? '' });
  if (c.evidence_label) rows.push({ key: 'evidence', label: 'Evidence', value: c.evidence_label });
  if (c.notes) rows.push({ key: 'notes', label: 'Notes', value: c.notes });
  return rows;
}

export interface HrDocumentsView {
  user_id: string; display_name: string; self: boolean;
  documents: HrDocument[];
  /** Newest first; the first is the current one. */
  rtw: HrRtwCheck[];
  /** Types the onboarding step asks this person to upload that aren't on file yet. */
  asked: HrDocType[];
  can: { upload: boolean; manage: boolean; set_visibility: boolean; record_rtw: boolean; reveal_share_code: boolean };
}

/** The permission behind expiry reminders and the Expiring soon list (also shows the notification setting). */
export const HR_EXPIRY_PERMISSION = 'hr.documents.manage';
export const HR_EXPIRY_KINDS = ['hr.document_expiring', 'hr.rtw_expiring'] as const;
/** Reminder text for one person's item; rolled up per recipient per day when there are several. */
export function hrExpiryTitle(items: { name: string; label: string; date: string; kind: 'document' | 'rtw' }[], today = hrToday()): { title: string; body: string } {
  if (items.length === 1) {
    const i = items[0]!;
    const when = hrExpiringLine(i, today);
    return { title: `${i.name}: ${i.label} - ${when.charAt(0).toLowerCase()}${when.slice(1)}`,
      body: i.kind === 'rtw' ? 'Their right to work is time-limited: do a follow-up check before then.' : 'Ask them for the new one and upload it to their Personal details.' };
  }
  return { title: `${items.length} HR documents need attention`, body: items.slice(0, 4).map(i => `${i.name}: ${i.label} (${hrDate(i.date)})`).join(', ') + (items.length > 4 ? ` and ${items.length - 4} more` : '') };
}
